import React from 'react';
import { Widget } from '@spins/react-ui-library';
import Updates from 'components/Updates';
import { PeriodEndWeekType } from 'utils/types';
import { titleCase } from 'utils/functions';
import Highlights from 'components/Highlights';
import {
  DISTRIBUTION_FILLED_KPI,
  DISTRIBUTION_KEPT_KPI,
  DISTRIBUTION_LOST_KPI,
  DISTRIBUTION_VOID_KPI,
  OPPORTUNITY_GAP_KPI,
  POD_KPI,
  SALES_KPI,
  STORES_SELLING_KPI,
  STORES_SELLING_PERC_KPI,
  UNITS_KPI,
  UNITS_STORES_SELLING_KPI,
  SALES_KPI_PORTFOLIO,
  UNITS_KPI_PORTFOLIO,
  POD_KPI_PORTFOLIO,
  STORES_SELLING_KPI_PORTFOLIO,
  STORES_GAP_KPI_PORTFOLIO,
  OPPORTUNITY_GAP_KPI_PORTFOLIO
} from 'apollo/queries';
import { HighlightValueFormat } from 'components/Highlight';
import HighlightQuery from 'components/HighlightQuery';

type HighlightType = 'overview' | 'voids' | 'accounts-items' | 'portfolio';

interface Props {
  clientId: string;
  currentBrand: string;
  globalFilter: any;
  periodEndWeek: PeriodEndWeekType;
  type?: HighlightType;
}

const SalesHighlightsWidget: React.FC<Props> = ({
  clientId,
  currentBrand,
  type,
  globalFilter,
  periodEndWeek
}) => {
  const renderPortfolio = () => {
    return [
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['DOLLARS']}
        query={SALES_KPI_PORTFOLIO}
        valueFormat={HighlightValueFormat.CURRENCY}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['UNITS']}
        query={UNITS_KPI_PORTFOLIO}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['POD']}
        query={POD_KPI_PORTFOLIO}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['STORES SELLING']}
        query={STORES_SELLING_KPI_PORTFOLIO}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['% OF STORES GAP']}
        query={STORES_GAP_KPI_PORTFOLIO}
        valueFormat={HighlightValueFormat.PERCENTAGE}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['$ OPPORTUNITY GAP']}
        query={OPPORTUNITY_GAP_KPI_PORTFOLIO}
        valueFormat={HighlightValueFormat.CURRENCY}
      />
    ].map((component, index) => React.cloneElement(component, { key: index }));
  };

  const renderOverview = () => {
    return [
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['DOLLARS']}
        query={SALES_KPI}
        valueFormat={HighlightValueFormat.CURRENCY}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['UNITS']}
        query={UNITS_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['POD']}
        query={POD_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['STORES SELLING']}
        query={STORES_SELLING_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['UNITS / STORES SELLING / WK']}
        query={UNITS_STORES_SELLING_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['$ OPPORTUNITY GAP']}
        query={OPPORTUNITY_GAP_KPI}
        valueFormat={HighlightValueFormat.CURRENCY}
      />
    ].map((component, index) => React.cloneElement(component, { key: index }));
  };

  const renderVoids = () => {
    return [
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['POD']}
        query={POD_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['$ OPPORTUNITY GAP']}
        query={OPPORTUNITY_GAP_KPI}
        valueFormat={HighlightValueFormat.CURRENCY}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['DISTRIBUTION KEPT']}
        query={DISTRIBUTION_KEPT_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['DISTRIBUTION LOST']}
        query={DISTRIBUTION_LOST_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['DISTRIBUTION VOID']}
        query={DISTRIBUTION_VOID_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['DISTRIBUTION FILLED']}
        query={DISTRIBUTION_FILLED_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />
    ].map((component, index) => React.cloneElement(component, { key: index }));
  };

  const renderAccountsItems = () => {
    return [
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['DOLLARS']}
        query={SALES_KPI}
        valueFormat={HighlightValueFormat.CURRENCY}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['UNITS']}
        query={UNITS_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['POD']}
        query={POD_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['STORES SELLING']}
        query={STORES_SELLING_KPI}
        valueFormat={HighlightValueFormat.NUMBER}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['% OF STORES GAP']}
        query={STORES_SELLING_PERC_KPI}
        valueFormat={HighlightValueFormat.PERCENTAGE}
      />,
      <HighlightQuery
        clientID={clientId}
        globalFilter={globalFilter}
        labels={['$ OPPORTUNITY GAP']}
        query={OPPORTUNITY_GAP_KPI}
        valueFormat={HighlightValueFormat.CURRENCY}
      />
    ].map((component, index) => React.cloneElement(component, { key: index }));
  };

  let items: Array<React.ReactElement>;
  switch (type) {
    case 'portfolio':
      items = renderPortfolio();
      break;
    case 'voids':
      items = renderVoids();
      break;
    case 'accounts-items':
      items = renderAccountsItems();
      break;
    case 'overview':
    default:
      items = renderOverview();
      break;
  }

  return (
    <Widget>
      {{
        header: {
          title: `Sales Highlights for "${titleCase(currentBrand)}"`,
          content: {
            right: <Updates nielsenDate={periodEndWeek.nielsen} spinsDate={periodEndWeek.spins} />
          }
        },
        body: {
          content: <Highlights>{items}</Highlights>
        }
      }}
    </Widget>
  );
};

export default SalesHighlightsWidget;
